/**
 * The lobby — everything before the game starts.
 *
 * Split out of `gameLink.ts` with the moves, and kept separate from them
 * because it is a different conversation: these ride the same connection (phase
 * 10 folded the old `gameService.ts` in here rather than opening a second
 * socket) but they act on the **party**, not on a round.
 *
 * ⚠️ Imported through the `services/gameLink.js` barrel, not directly, and not
 * by a component at all — see the eslint boundary in `web/eslint.config.ts`.
 */

import type { Ack, ConfigureRequest, SelectionOptions } from "../generated/client.js";
import type { GameLink } from "./types.js";

// ---------------------------------------------------------------------------
// The lobby — phase 10. Absorbed from the phase-3 `gameService.ts`, which was
// written against a device token script can no longer read and had no callers.
// ---------------------------------------------------------------------------

/**
 * Change the party's settings. **Host only** — anybody else is refused
 * server-side, which is why the lobby renders these controls for the host alone
 * rather than offering a button that always fails.
 */
export async function configureParty(
  link: GameLink,
  settings: ConfigureRequest,
): Promise<Ack> {
  return link.client.party.configure(settings);
}

/** Say whether you are ready. Everybody may do this for themselves. */
export async function setReady(link: GameLink, ready: boolean): Promise<Ack> {
  return link.client.party.ready({ ready });
}

/** Create a team. Host only. */
export async function createTeam(link: GameLink, name: string): Promise<Ack> {
  return link.client.party.create_team({ name });
}

/**
 * Join a team, or `null` to leave one.
 *
 * Anyone who can act moves **themselves** — there is no field for whose team to
 * change, the same way no game intent has a field for whose turn to take.
 */
export async function joinTeam(link: GameLink, teamId: string | null): Promise<Ack> {
  return link.client.party.join_team({ team_id: teamId });
}

/**
 * What the lobby's picker may offer — phase 16h, scoped in 16g-2.
 *
 * Genres flat with `parent_id`, because **grouping is the UI's job**: the
 * backend holds data and the screen decides how to present it. Decades come
 * with them because an installation may serve only some of them, and two round
 * trips for one screen is two chances to render half of it.
 */
export async function listSelectionOptions(link: GameLink): Promise<SelectionOptions> {
  return link.client.catalog.options();
}

// ---------------------------------------------------------------------------
// What the party deals from beyond the local catalog — phase 19e-2b, given a
// caller in 19e-4. Every one of these existed on the server for two sub-phases
// with nothing on this side able to reach it.
// ---------------------------------------------------------------------------

/**
 * Add a playlist this party will deal from.
 *
 * ⚠️ **Anybody who can act, not just the host.** A public playlist is readable
 * with an app token and no user OAuth, so "send a link" is the free way to
 * contribute and requiring an account would give away the one property that
 * makes the source model cheap. The server owns that rule; this only has to
 * avoid contradicting it by hiding the control.
 *
 * Takes whatever somebody pasted — a share link, a `spotify:` URI, or a bare id
 * — because the server parses it and answers a bad one with its own sentence.
 * Validating the shape here would be a second parser to keep in step.
 */
export async function addPlaylist(link: GameLink, pasted: string): Promise<Ack> {
  return link.client.party.add_playlist({ link: pasted });
}

/**
 * Contribute your own liked songs, or stop.
 *
 * 🔴 **Only ever your own**, and the request carries no account id precisely so
 * that one member cannot volunteer another's saved songs — which would also
 * spend one of the five development-mode OAuth seats on their behalf.
 */
export async function contributeLibrary(
  link: GameLink,
  contribute: boolean,
): Promise<Ack> {
  return link.client.party.contribute_library({ contribute });
}

/** Take a source away. The host may remove any; anybody may remove their own. */
export async function removeSource(
  link: GameLink,
  kind: string,
  refId: string,
): Promise<Ack> {
  return link.client.party.remove_source({ kind, ref_id: refId });
}

/**
 * Queue a background refresh of every Spotify source. **Host only.**
 *
 * ⚠️ Returns as soon as the job is queued. Ten playlists is up to ninety
 * paginated requests against a rate-limited API, so nothing about this is
 * synchronous and the lobby must not render it as though a number will change
 * when the promise resolves.
 */
export async function refreshSources(link: GameLink): Promise<Ack> {
  return link.client.party.refresh_sources({ ready: true });
}
